// Stoppage — generic "rule was broken, whistle blows" routing.
//
// Owns:   the transition from Play (or Faceoff/etc.) into the Stoppage
//         phase and ultimately into a Faceoff at a specific dot.
// Reads:  none directly — caller supplies the stoppage spot + reason.
// Mutates: state.clock (phase=Stoppage, remainingTicks),
//          state.puck (mode=Loose at the dot, vel=0, lastTouchedBy unchanged),
//          state.rules.nextFaceoffSpot.
// Emits:  Stoppage event.
//
// Every rule that calls for a faceoff (offside, icing, goalie freeze,
// puck-out-of-play, period start) routes through `enterStoppage`. The
// caller picks the spot; this helper handles the phase + puck reset.
//
// Phase flow:  Play → Stoppage (60 ticks) → Faceoff (60 ticks) → Play
//
// The Stoppage window is a "whistle blowing" pause where the puck is
// dead at the dot but players haven't been snapped to the faceoff
// layout yet — gives a visible beat before the formal faceoff. After
// the Stoppage timer expires, the match-clock step (Step 18) snaps
// players to the per-spot layout and transitions to Faceoff.
//
// Zero-allocation.

import { type FaceoffSpot, faceoffSpotInto } from "../state/faceoff-spots.js";
import type { MatchState } from "../state/types.js";

export type StoppageReason = "Offside" | "Icing" | "GoalieFreeze" | "PuckOutOfPlay" | "Manual";

const SPOT_SCRATCH = { x: 0, y: 0 };

// 60 ticks = 1 second of "whistle blowing" before the faceoff layout
// snap. Tunable later if it feels too short / too long.
const STOPPAGE_TICKS = 60;

/**
 * Drop play into a Stoppage phase that resolves to a faceoff at `spot`.
 * Idempotent on rapid double-calls (a second call mid-Stoppage just
 * updates the target spot + emits a fresh event).
 *
 * Does NOT snap players to the faceoff layout yet — that happens at the
 * Stoppage → Faceoff transition in `core/SimTick.ts` Step 18. The
 * intervening Stoppage ticks freeze the puck dead at the dot but leave
 * players where they were when the whistle blew, which reads visually
 * as "play is dead, the ref is calling it."
 */
export function enterStoppage(state: MatchState, spot: FaceoffSpot, reason: StoppageReason): void {
  faceoffSpotInto(SPOT_SCRATCH, spot);
  state.puck.pos.x = SPOT_SCRATCH.x;
  state.puck.pos.y = SPOT_SCRATCH.y;
  state.puck.vel.x = 0;
  state.puck.vel.y = 0;
  state.puck.mode = "Loose";
  state.puck.ownerId = null;
  // Lock the puck for the full Stoppage + Faceoff window. faceoffPauseTicks
  // is the Faceoff pause length; STOPPAGE_TICKS is the whistle window.
  // Reusing `noRecaptureUntilTick` keeps the puck untouchable.
  state.puck.noRecaptureUntilTick = state.tick + STOPPAGE_TICKS + 60;

  state.clock.phase = "Stoppage";
  state.clock.remainingTicks = STOPPAGE_TICKS;
  state.rules.nextFaceoffSpot = spot;

  state.events.push({
    type: "Stoppage",
    tick: state.tick,
    spot,
    reason,
  });
}

export { STOPPAGE_TICKS };
